import React, { useEffect, useState } from 'react';
import { EditableTitle } from 'src/components/atoms';
import { StyledIcon } from 'src/components/foundations/foundations.styles';
import { CategoryType } from 'src/graphql';
import { useTheme } from 'styled-components';
import { CategoryNameStyled, DragIcon } from './CategoryName.styles';

interface Props {
  category: CategoryType;
  currentLocale: string;
  onSave: (id: number, name: string, setName: React.Dispatch<string>) => void;
  defaultValue?: string;
  maxNameLength?: number;
  onAtMaxNameLengthChange?: (atMax: boolean) => void;
  disabled?: boolean;
}

export const CategoryName = ({
  category,
  currentLocale,
  onSave,
  defaultValue,
  maxNameLength,
  onAtMaxNameLengthChange,
  disabled,
}: Props) => {
  const theme = useTheme();
  const nameFromProps = category.locales?.[currentLocale]?.name;

  const [name, setName] = useState<string>(nameFromProps ?? '');

  useEffect(() => {
    setName(nameFromProps ?? '');
  }, [nameFromProps]);

  useEffect(() => {
    if (maxNameLength == null) {
      onAtMaxNameLengthChange?.(false);
      return;
    }
    const length = (name ?? '').length;
    const syncedName = nameFromProps ?? '';
    const atLimit = length >= maxNameLength;

    const draftDiffersFromSaved = (name ?? '') !== syncedName;
    onAtMaxNameLengthChange?.(atLimit && draftDiffersFromSaved);
  }, [name, maxNameLength, onAtMaxNameLengthChange, nameFromProps]);

  return (
    <CategoryNameStyled>
      <DragIcon>
        <StyledIcon
          size="20px"
          className="uil-arrows-v"
          color={theme.atlas.semantic.color.type.default}
        />
      </DragIcon>
      <EditableTitle
        value={name ?? ''}
        setValue={setName}
        defaultValue={defaultValue ?? 'Untitled Category'}
        disabled={disabled}
        onSave={() => onSave(category.id, name ?? '', setName)}
        data-testid="category-name"
        maxLength={maxNameLength}
      />
    </CategoryNameStyled>
  );
};
